import { PERMISSIONS } from '@didto/domain';
import {
  Badge,
  Button,
  ErrorState,
  Field,
  Input,
  KvRow,
  Loading,
  Notice,
  PageHeader,
  SectionHead,
} from '@didto/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSession } from '@/app/session';
import { errorText } from '@/lib/errors';
import { useOnline } from '@/lib/network';
import { supabase } from '@/lib/supabase';
import './settings.css';

interface Factor {
  id: string;
  friendly_name?: string;
  status: 'verified' | 'unverified';
  created_at: string;
}

/** What an assurance level means, next to its code. */
const AAL_WORDS: Record<string, string> = {
  aal1: 'password only',
  aal2: 'password and authenticator code',
};

/** TOTP enrollment for the signed-in owner/manager. Uses Supabase Auth MFA (available on Free). */
export function SecurityPage() {
  const s = useSession();
  const qc = useQueryClient();
  // ADR-037: setting up or removing an authenticator needs the server
  const online = useOnline();
  const factorsQuery = useQuery({
    queryKey: ['mfa-factors'],
    queryFn: async () => {
      const { data } = await supabase.auth.mfa.listFactors();
      return (data?.totp ?? []) as Factor[];
    },
  });
  const factors = factorsQuery.data ?? [];
  const [enrolling, setEnrolling] = useState<{ id: string; qr: string; secret: string } | null>(
    null,
  );
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    await qc.invalidateQueries({ queryKey: ['mfa-factors'] });
  };

  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: err } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'didto. OS',
      });
      if (err || !data) {
        setError(err ? errorText(err) : 'Could not start enrolment');
        return;
      }
      setEnrolling({ id: data.id, qr: data.totp.qr_code, secret: data.totp.secret });
    } catch (x) {
      setError(errorText(x));
    } finally {
      setBusy(false);
    }
  };
  const verify = async () => {
    if (!enrolling) return;
    setBusy(true);
    setError(null);
    const { error: err } = await supabase.auth.mfa.challengeAndVerify({
      factorId: enrolling.id,
      code: code.trim(),
    });
    setBusy(false);
    if (err) {
      setError('Code not accepted. Try the next code from your app.');
      return;
    }
    setEnrolling(null);
    setCode('');
    await load();
    await s.refresh();
  };
  const remove = async (id: string) => {
    setBusy(true);
    const { error: err } = await supabase.auth.mfa.unenroll({ factorId: id });
    setBusy(false);
    if (err) {
      setError(errorText(err));
      return;
    }
    await load();
    await s.refresh();
  };

  const verified = factors.filter((f) => f.status === 'verified');
  // the session's permissions in words, with their keys (in the order the domain lists them)
  const held = PERMISSIONS.filter((p) => s.permissions.has(p.key));
  const unlisted = [...s.permissions].filter((k) => !PERMISSIONS.some((p) => p.key === k)).sort();
  const aal = s.me?.aal ?? null;
  return (
    <div className="d-page">
      <PageHeader
        eyebrow="Settings"
        title="Security & MFA"
        subtitle="Two-step verification for owner and manager logins. Café staff use PINs on enrolled tablets and are not affected."
      />
      <section className="d-surface d-surface--pad d-stack">
        <SectionHead title="Authenticator app (TOTP)">
          {verified.length > 0 ? (
            <Badge tone="success" dot>
              enabled
            </Badge>
          ) : (
            <Badge tone="warning">not enabled</Badge>
          )}
        </SectionHead>
        <p className="d-muted">
          Once verified, this login must confirm a 6-digit code at sign-in, and the server refuses
          sensitive actions without it.
        </p>
        {factorsQuery.isLoading && <Loading label="Loading authenticators…" />}
        {factorsQuery.isError && (
          <ErrorState
            title="Could not load the authenticators"
            message={errorText(factorsQuery.error)}
            onRetry={() => void factorsQuery.refetch()}
          />
        )}
        {verified.length > 0 && (
          <div className="d-list">
            {verified.map((f) => (
              <div key={f.id} className="d-row">
                <div className="d-row__main">
                  <div className="d-row__title">{f.friendly_name ?? 'Authenticator'}</div>
                  <div className="d-row__meta">
                    added {new Date(f.created_at).toLocaleDateString()}
                  </div>
                </div>
                <div className="d-actions">
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={!online || busy}
                    onClick={() => void remove(f.id)}
                  >
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {!enrolling && (
          <div>
            <Button
              variant="primary"
              busy={busy}
              disabled={!online}
              onClick={() => void start()}
              data-testid="mfa-enroll"
            >
              {verified.length ? 'Add another authenticator' : 'Enable two-step verification'}
            </Button>
          </div>
        )}
        {enrolling && (
          <div className="security__enrol">
            <p className="d-muted">
              Scan this with Google Authenticator, 1Password, Authy or similar, then enter the code
              it shows.
            </p>
            <img
              src={enrolling.qr}
              alt="TOTP QR code"
              width={200}
              height={200}
              className="security__qr"
            />
            <details className="security__secret">
              <summary>Can't scan? Show the secret</summary>
              <code className="d-mono security__secret-code" data-testid="mfa-secret">
                {enrolling.secret}
              </code>
            </details>
            <div className="security__verify">
              <Field label="6-digit code" error={error}>
                {(id) => (
                  <Input
                    id={id}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="security__code"
                    data-testid="mfa-verify-code"
                  />
                )}
              </Field>
            </div>
            <div className="d-actions security__actions">
              <Button
                variant="primary"
                busy={busy}
                disabled={!online || code.length < 6}
                onClick={() => void verify()}
                data-testid="mfa-verify-submit"
              >
                Verify and enable
              </Button>
              <Button
                variant="quiet"
                onClick={() => {
                  setEnrolling(null);
                  setError(null);
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}
        {error && !enrolling && (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        )}
      </section>
      <section className="d-surface d-surface--pad d-stack">
        <SectionHead title="Session" />
        <div className="d-kv">
          <KvRow label="Signed in as" value={s.me?.employee?.display_name ?? '—'} />
          <KvRow
            label="Assurance level"
            value={
              <>
                {aal && AAL_WORDS[aal] && <span>{AAL_WORDS[aal]} · </span>}
                <span className="d-mono d-faint">{aal ?? '—'}</span>
              </>
            }
          />
          <KvRow label="Permissions" value={<span className="d-num">{s.permissions.size}</span>} />
        </div>
        {s.permissions.size > 0 && (
          <details className="settings__perms">
            <summary>Show this session’s permissions</summary>
            <ul className="settings__perm-list">
              {held.map((p) => (
                <li key={p.key}>
                  <span title={p.key}>{p.description}</span>
                </li>
              ))}
              {unlisted.map((k) => (
                <li key={k}>
                  <span className="d-mono d-faint">{k}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </section>
    </div>
  );
}
